import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  CheckCircle2,
  Hammer,
  ImageIcon,
  Mail,
  MessageCircle,
  Package,
  Truck,
} from 'lucide-react'
import { useUsersApi } from 'src/api/db/apiUsers'
import {
  useAdminPaginatedQuotes,
  useAdminRequestsOverviewQuotes,
  type QuoteListRow,
} from 'src/api/db/apiQuote'
import {
  adminOrdersPath,
  buildAdminOrderPath,
  paths,
} from 'src/routes/paths'
import { PageContainer } from 'src/shared/components/PageContainer'
import { FullScreenLoader } from 'src/shared/components/FullScreenLoader'
import { Column } from 'src/shared/components/Semantic/all'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import {
  ACTIVE_QUOTE_STATUSES,
  effectiveQuoteStatus,
} from 'src/shared/consts/quoteStatus'
import { capitalize } from 'src/shared/utils'
import { useEmailInboxList } from 'src/api/db/apiEmailInbox'
import { ActionCard, type ActionTone } from './components/ActionCard'
import { DashboardSection } from './components/DashboardSection'

type AttentionReason =
  | 'align'
  | 'proofs'
  | 'production'
  | 'shipping'

const ATTENTION_LABELS: Record<
  AttentionReason,
  { en: string; sv: string; icon: typeof MessageCircle; tone: ActionTone }
> = {
  align: {
    en: 'Align on quote',
    sv: 'Stäm av offert',
    icon: MessageCircle,
    tone: 'blue',
  },
  proofs: {
    en: 'Upload proofs',
    sv: 'Ladda upp korrektur',
    icon: ImageIcon,
    tone: 'orange',
  },
  production: {
    en: 'Start production',
    sv: 'Starta produktion',
    icon: Hammer,
    tone: 'amber',
  },
  shipping: {
    en: 'Update shipping',
    sv: 'Uppdatera leverans',
    icon: Truck,
    tone: 'teal',
  },
}

const attentionReasonFor = (
  quote: QuoteListRow
): AttentionReason | null => {
  const status = effectiveQuoteStatus(quote.status)
  if (status === '1_contacted_sales') return 'align'
  // Ready for customer approval — no sales action until they approve
  if (status === '2_approved_quote') return 'proofs'
  if (status === '3_approved_proofs') return 'production'
  if (status === '5_awaiting_shipment' || status === 'awaiting_shipment') {
    return 'shipping'
  }
  return null
}

const ACTION_LIMIT = 8

const greetingForHour = (hour: number, t: (en: string, sv: string) => string) => {
  if (hour < 12) return t('Good morning', 'God morgon')
  if (hour < 17) return t('Good afternoon', 'God eftermiddag')
  return t('Good evening', 'God kväll')
}

export const AdminDashboardPage = () => {
  const { t } = useLocalization()
  const navigate = useNavigate()
  const { user } = useUsersApi()

  const { data: pageResult, isPending: isLoadingQuotes } =
    useAdminPaginatedQuotes({
      page: 0,
      pageSize: 60,
      statuses: [...ACTIVE_QUOTE_STATUSES],
      excludeAnon: true,
      hasItemsOnly: true,
    })

  const { data: needsResponseResult, isPending: isLoadingNeedsResponse } =
    useAdminRequestsOverviewQuotes({
      page: 0,
      pageSize: ACTION_LIMIT,
      responseFilter: 'needs_response',
    })

  const { data: inboxRows, isPending: isLoadingInbox } = useEmailInboxList({
    status: 'needs_action',
    functionTag: null,
    search: '',
  })

  const activeQuotes = pageResult?.data ?? []
  const needsResponseQuotes = needsResponseResult?.data ?? []
  const needsResponseTotal = needsResponseResult?.totalCount ?? 0

  const orderActions = useMemo(() => {
    return activeQuotes
      .map((quote) => {
        const reason = attentionReasonFor(quote)
        if (!reason) return null
        return { quote, reason }
      })
      .filter(Boolean) as Array<{
      quote: QuoteListRow
      reason: AttentionReason
    }>
  }, [activeQuotes])

  const orderActionItems = orderActions.slice(0, ACTION_LIMIT)
  const orderActionTotal = orderActions.length
  const inboxActions = (inboxRows ?? []).slice(0, ACTION_LIMIT)
  const inboxTotal = inboxRows?.length ?? 0
  const totalActionCount = needsResponseTotal + orderActionTotal + inboxTotal
  const isLoading = isLoadingQuotes || isLoadingNeedsResponse || isLoadingInbox

  const firstName = user?.data?.first_name
    ? capitalize(user.data.first_name)
    : null
  const hello = greetingForHour(new Date().getHours(), t)
  const headline = firstName ? `${hello}, ${firstName}` : hello

  const queueLine = t(
    `${needsResponseTotal} chats, ${inboxTotal} emails, ${orderActionTotal} orders in the queue`,
    `${needsResponseTotal} chattar, ${inboxTotal} mejl, ${orderActionTotal} ordrar i kön`
  )

  if (isLoading) {
    return <FullScreenLoader />
  }

  return (
    <PageContainer className="gap-8">
      <Column className="gap-1">
        <h1>{headline}</h1>
        <p className="text-sm text-muted-foreground">{queueLine}</p>
      </Column>

      {totalActionCount === 0 ? (
        <Column className="items-center gap-3 rounded-[var(--radius-outer)] border border-emerald-200/80 bg-gradient-to-br from-emerald-50 via-white to-teal-50/60 px-6 py-16 text-center shadow-soft">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-emerald-400 to-teal-600 text-white shadow-chip">
            <CheckCircle2 className="h-7 w-7" strokeWidth={2} />
          </div>
          <Column className="gap-1">
            <h2>{t('All caught up', 'Allt är ikapp')}</h2>
            <p className="text-sm text-muted-foreground">
              {t(
                'No chats, emails, or orders waiting on you right now.',
                'Inga chattar, mejl eller ordrar väntar på dig just nu.'
              )}
            </p>
          </Column>
        </Column>
      ) : (
        <Column className="gap-6">
          <DashboardSection
            title={t('Chats waiting for you', 'Chattar som väntar på dig')}
            description={t(
              'Customer messages and team pings that need a reply.',
              'Kundmeddelanden och team-pingar som behöver svar.'
            )}
            count={needsResponseTotal}
            icon={MessageCircle}
            accent="violet"
            viewAllHref={adminOrdersPath}
            viewAllLabel={t('View all requests', 'Visa alla förfrågningar')}
          >
            {needsResponseQuotes.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {t(
                  'No chats waiting for a reply.',
                  'Inga chattar väntar på svar.'
                )}
              </p>
            ) : (
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                {needsResponseQuotes.map((quote) => (
                  <ActionCard
                    key={quote.id}
                    icon={MessageCircle}
                    tone="violet"
                    quote={quote}
                    title={
                      quote.name ||
                      t('Untitled request', 'Namnlös förfrågan')
                    }
                    subtitle={quote.organization?.name ?? undefined}
                    badge={t('Needs reply', 'Behöver svar')}
                    onClick={() =>
                      navigate(buildAdminOrderPath(quote.public_id ?? ''))
                    }
                  />
                ))}
              </div>
            )}
          </DashboardSection>

          <DashboardSection
            title={t('Emails waiting for you', 'Mejl som väntar på dig')}
            description={t(
              'Customer, finance, and supplier threads with a suggested reply.',
              'Kund-, ekonomi- och leverantörstrådar med ett föreslaget svar.'
            )}
            count={inboxTotal}
            icon={Mail}
            accent="orange"
            viewAllHref={paths.adminEmail}
            viewAllLabel={t('Open inbox', 'Öppna inkorgen')}
          >
            {inboxActions.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {t(
                  'No emails waiting for a reply.',
                  'Inga mejl väntar på svar.'
                )}
              </p>
            ) : (
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                {inboxActions.map((row) => (
                  <ActionCard
                    key={row.id}
                    icon={Mail}
                    tone="orange"
                    title={
                      row.email_thread?.subject ||
                      t('No subject', 'Inget ämne')
                    }
                    subtitle={
                      row.email_thread?.organization?.name ||
                      row.email_thread?.email_contact?.email ||
                      undefined
                    }
                    badge={t('Needs reply', 'Behöver svar')}
                    onClick={() =>
                      navigate(
                        `${paths.adminEmail}?thread=${row.email_thread?.id ?? ''}`
                      )
                    }
                  />
                ))}
              </div>
            )}
          </DashboardSection>

          <DashboardSection
            title={t('Orders waiting on you', 'Ordrar som väntar på dig')}
            description={t(
              'Pipeline steps that need sales or ops action.',
              'Pipeline-steg som behöver sälj- eller ops-åtgärd.'
            )}
            count={orderActionTotal}
            icon={Package}
            accent="blue"
            viewAllHref={adminOrdersPath}
            viewAllLabel={t('Open pipeline', 'Öppna pipeline')}
          >
            {orderActionItems.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {t(
                  'No orders waiting on sales right now.',
                  'Inga ordrar väntar på sälj just nu.'
                )}
              </p>
            ) : (
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                {orderActionItems.map(({ quote, reason }) => {
                  const meta = ATTENTION_LABELS[reason]
                  return (
                    <ActionCard
                      key={quote.id}
                      icon={meta.icon}
                      tone={meta.tone}
                      quote={quote}
                      title={
                        quote.name ||
                        t('Untitled request', 'Namnlös förfrågan')
                      }
                      subtitle={quote.organization?.name ?? undefined}
                      badge={t(meta.en, meta.sv)}
                      onClick={() =>
                        navigate(buildAdminOrderPath(quote.public_id ?? ''))
                      }
                    />
                  )
                })}
              </div>
            )}
          </DashboardSection>
        </Column>
      )}
    </PageContainer>
  )
}
